import LegalPage, { LList, EmailLink } from "@/components/LegalPage";

const SECTIONS = [
  {
    title: "1. Information We Collect",
    body: (
      <>
        <p>We may collect the following information when you use Abhyas:</p>
        <LList
          items={[
            "Name",
            "Email address, where provided",
            "Phone number, where provided",
            "Account and login information",
            "Class/grade",
            "Board (CBSE/ICSE/ISC)",
            "Subjects and chapters/topics you select",
            "Practice questions and answers generated through the platform",
            "Practice activity, progress, streaks, usage history, and preferences",
            "Subscription/payment-related information necessary to manage subscriptions",
            "Device/browser information and basic technical information",
            "Cookies and analytics information",
          ]}
        />
        <p>
          Payments on Abhyas are processed through a third-party payment
          gateway. We do not store your complete card, UPI, or banking
          credentials on our systems.
        </p>
      </>
    ),
  },
  {
    title: "2. How We Use Information",
    body: (
      <>
        <p>Information we collect may be used to:</p>
        <LList
          items={[
            "Create and manage your account",
            "Provide personalized practice",
            "Generate AI-based practice questions and solved answers",
            "Track learning progress and activity",
            "Maintain streaks and dashboard statistics",
            "Process subscriptions and payments",
            "Provide customer support",
            "Improve reliability, functionality, and user experience",
            "Detect abuse, fraud, or security issues",
            "Communicate important service-related updates",
          ]}
        />
      </>
    ),
  },
  {
    title: "3. AI-Generated Content",
    body: (
      <p>
        Abhyas uses AI technology to generate educational practice content,
        including questions, hints, and step-by-step solutions. To provide this
        functionality, your academic preferences (such as board, class, subject,
        and chapter selections) and practice inputs may be processed by the
        systems and services required to deliver the AI features.
      </p>
    ),
  },
  {
    title: "4. Payment Information",
    body: (
      <p>
        Payments may be handled by third-party payment processors (such as
        Razorpay or another authorized provider). These processors handle your
        payment credentials under their own privacy policies and security
        standards. Abhyas does not directly store complete payment-card or
        banking credentials.
      </p>
    ),
  },
  {
    title: "5. Cookies and Analytics",
    body: (
      <p>
        Abhyas uses necessary cookies and similar technologies to keep you
        signed in and maintain the service. Where applicable, we may also use
        analytics technologies to understand usage patterns, improve
        performance, and maintain the platform.
      </p>
    ),
  },
  {
    title: "6. Data Storage and Security",
    body: (
      <p>
        We use reasonable technical and organizational measures to protect user
        information from unauthorized access, loss, or misuse. However, no
        method of transmission or storage is completely secure, and we cannot
        promise absolute security.
      </p>
    ),
  },
  {
    title: "7. Data Sharing",
    body: (
      <>
        <p>Information may be shared only when necessary with:</p>
        <LList
          items={[
            "Service providers that help operate Abhyas",
            "Payment processors",
            "Hosting/cloud infrastructure providers",
            "AI/API providers required to provide the service",
            "Analytics providers, where applicable",
            "Legal or regulatory authorities where legally required",
          ]}
        />
        <p>Abhyas does not sell personal information to third parties.</p>
      </>
    ),
  },
  {
    title: "8. Data Retention",
    body: (
      <p>
        We retain information only for as long as reasonably necessary to
        provide the service, meet legal obligations, resolve disputes, maintain
        security, and serve legitimate business purposes.
      </p>
    ),
  },
  {
    title: "9. User Rights and Data Deletion",
    body: (
      <p>
        You may request access to, correction of, or deletion of your personal
        information, subject to applicable legal requirements. To make a
        request, contact us at <EmailLink />.
      </p>
    ),
  },
  {
    title: "10. Children's / Student Privacy",
    body: (
      <p>
        Abhyas is an educational platform that may be used by school students.
        Students should use the platform with appropriate parental or guardian
        involvement where required by applicable law. Abhyas does not knowingly
        seek unnecessary personal information from children.
      </p>
    ),
  },
  {
    title: "11. Third-Party Links and Services",
    body: (
      <p>
        Abhyas may use or link to third-party services (for example, payment
        processors or authentication providers). These services operate under
        their own privacy policies, which we encourage you to review.
      </p>
    ),
  },
  {
    title: "12. Changes to This Privacy Policy",
    body: (
      <p>
        We may update this Privacy Policy from time to time. The updated
        version will be posted on this page, and continued use of Abhyas after
        changes are posted means you accept the updated policy.
      </p>
    ),
  },
  {
    title: "13. Contact",
    body: (
      <p>
        For any privacy-related questions or requests, contact us at{" "}
        <EmailLink />.
      </p>
    ),
  },
];

const PrivacyPolicy = () => (
  <LegalPage
    testid="privacy-policy-page"
    title="Privacy Policy"
    intro="Abhyas respects your privacy. This policy explains how information is collected, used, stored, and protected when students use the Abhyas platform."
    sections={SECTIONS}
  />
);

export default PrivacyPolicy;
